import { mediaQueryLarge } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    // Mobile toggle button may render in a separate filter bar section outside this component
    document.addEventListener('click', this.#handleGridToggleClick);

    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener('click', this.#handleGridToggleClick);
  }

  /**
   * Updates the layout from a desktop radio input change.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#setLayout(target.value);
  }

  /**
   * Toggles the mobile layout between one and two columns.
   */
  handleLayoutToggle() {
    const { grid } = this.refs;
    if (!grid) return;

    const current = grid.getAttribute('product-grid-view') || 'two';
    const next = current === 'one' ? 'two' : 'one';

    this.#setLayout(next);
    this.#syncToggleIcons(next);

    localStorage.setItem('grid-layout', next);
  }

  /**
   * Syncs the .is-active class on toggle icon spans to reflect the current layout.
   *
   * @param {string} value - 'one' or 'two'
   */
  #syncToggleIcons(value) {
    document.querySelectorAll('.column-toggle__icon--one, .column-toggle__icon--two').forEach((icon) => {
      icon.classList.toggle('is-active', icon.classList.contains(`column-toggle__icon--${value}`));
    });
  }

  /**
   * Sets the product-grid-view attribute on the grid element.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handle mobile layout toggle button clicks from anywhere in the document.
   * Only acts when the button is outside this component (inside-component clicks are handled by on:click delegation).
   *
   * @param {Event} event
   */
  #handleGridToggleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const toggle = target.closest('.column-toggle');
    if (!toggle) return;
    if (this.contains(toggle)) return;
    this.handleLayoutToggle();
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    if (event.matches) {
      const targetElement = this.querySelector('[data-grid-layout="desktop-default-option"]');
      if (targetElement instanceof HTMLInputElement) {
        targetElement.checked = true;
      }
      this.#setLayout('default');
    } else {
      this.#setLayout('two');
      this.#syncToggleIcons('two');
    }
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
